import type { ThemeVariant } from '../../contract.js';

// Sidebar palette is SHARED across variants on purpose so flipping mode
// doesn't recolour the admin shell chrome — only the main content area.
const SIDEBAR_DARK = {
  sidebar: '#0F0F10',
  sidebarText: '#F4F4F2',
  sidebarMute: '#8F8F8B',
  sidebarActive: '#22C55E',
};

const RADII = { sm: '6px', md: '10px', lg: '14px', xl: '20px' };

// `mono` now mirrors `sans`. See packages/skins/src/styles.css for
// rationale — modern sans fonts support OpenType `tnum` for tabular
// alignment without swapping to a monospace face with a slashed zero.
const SANS_STACK = "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";
const FONTS = {
  sans: SANS_STACK,
  mono: SANS_STACK,
};

const MOTION = {
  fill: 'cubic-bezier(0.22, 1, 0.36, 1)',
  flow: 'linear',
  pulse: 'ease-out',
  press: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

export const defaultLight: ThemeVariant = {
  mode: 'light',
  tokens: {
    colors: {
      bg: '#F7F5F0',
      surface: '#FFFFFF',
      surface2: '#FAF9F5',
      ...SIDEBAR_DARK,
      border: '#E5E4DE',
      borderStrong: '#D4D3CC',
      // Light-mode accent deepened from green-500 (#22C55E) to green-600
      // (#16A34A). The brighter shade reads as confident on dark surfaces
      // but screams on pure white backgrounds (CTAs, charging ring, sidebar
      // active state). The translucent `accentSoft` still uses the brighter
      // green tint so hover/pressed backgrounds keep their soft character.
      accent: '#16A34A',
      accentSoft: 'rgba(34, 197, 94, 0.12)',
      // Default light's accent is a mid-luminance green; white text
      // reads cleanly on it and matches the long-standing StickyCTA
      // literal. Themes promoted from this spread (cartographic,
      // neon light, obsidian light) inherit this value.
      onAccent: '#FFFFFF',
      // Charging surface color deepened to emerald-600 so it matches
      // the new accent depth instead of looking lighter than the static
      // accent under the same kind of light.
      charging: '#059669',
      chargingGlow: 'rgba(34, 197, 94, 0.45)',
      power: '#CA8A04',
      info: '#2563EB',
      warn: '#DC2626',
      good: '#16A34A',
      busy: '#D97706',
      mute: '#8B8B85',
      text: '#1A1A1A',
      textMute: '#5F5F5A',
      // Light mode glows run at lower alphas than dark mode so the green
      // reads as a tint against the cream surface instead of looking
      // smudgy. Color-mix source tracks the new deeper accent so the
      // glow hue stays consistent with the on-screen accent elements.
      cardGlow:
        '0 0 0 1px color-mix(in srgb, #16A34A 14%, transparent),' +
        ' 0 0 18px -4px color-mix(in srgb, #16A34A 18%, transparent),' +
        ' 0 6px 20px -10px color-mix(in srgb, #16A34A 16%, transparent)',
      cardGlowSubtle:
        '0 0 0 1px color-mix(in srgb, #16A34A 7%, transparent),' +
        ' 0 0 10px -6px color-mix(in srgb, #16A34A 10%, transparent),' +
        ' 0 3px 10px -8px color-mix(in srgb, #16A34A 7%, transparent)',
      // Light-mode widget chrome. White-at-78% bg gives the cards
      // a frosted-paper feel over the light basemap; the 1px green-
      // ring on the shadow keeps the brand edge that dark mode
      // already has. Reduced blur (8px vs dark's 16px) — heavy
      // backdrop-filter on a bright basemap reads as muddy fog.
      widgetBg: 'rgba(255, 255, 255, 0.78)',
      widgetBorder: 'rgba(0, 0, 0, 0.08)',
      widgetShadow:
        '0 8px 24px rgba(0, 0, 0, 0.12),' +
        ' 0 0 0 1px rgba(22, 163, 74, 0.10)',
      widgetBlur: 'blur(8px) saturate(110%)',
      // Light-mode chrome tier — popovers, dropdowns, side panels.
      // Near-white at 94% so menu content reads solid over the page
      // bg without the muddy fog heavy blur produces; reduced blur
      // (12px vs dark's 16px) for the same reason.
      chromeBg: 'rgba(252, 252, 254, 0.94)',
      chromeBorder: 'rgba(0, 0, 0, 0.06)',
      chromeShadow: '0 4px 16px rgba(0, 0, 0, 0.10)',
      chromeBlur: 'blur(12px) saturate(110%)',
    },
    radii: RADII,
    fonts: FONTS,
    motion: MOTION,
  },
};

export const defaultDark: ThemeVariant = {
  mode: 'dark',
  tokens: {
    colors: {
      // Lifted surface values so cards visibly sit on top of the page
      // rather than camouflaging into it. Page ↔ surface delta is ~12
      // luminance points.
      bg: '#0E1013',
      surface: '#1A1D22',
      surface2: '#23272E',
      ...SIDEBAR_DARK,
      border: '#2D313A',
      borderStrong: '#3F434D',
      accent: '#22C55E',
      accentSoft: 'rgba(34, 197, 94, 0.18)',
      // Default dark's accent green-500; same white-on-green CTA
      // pattern as the light variant. Inherited by cartographic dark,
      // neon dark, obsidian dark via spread.
      onAccent: '#FFFFFF',
      charging: '#22C55E',
      chargingGlow: 'rgba(34, 197, 94, 0.55)',
      power: '#FACC15',
      info: '#60A5FA',
      warn: '#F87171',
      good: '#34D399',
      busy: '#F59E0B',
      mute: '#6B6B6B',
      text: '#F3F4F6',
      textMute: '#A1A1AA',
      // Dark mode only — an accent rim plus a soft green bloom sitting
      // around each card. `cardGlowSubtle` is the half-strength version
      // used on the PWA profile menu + points card.
      cardGlow:
        '0 0 0 1px color-mix(in srgb, #22C55E 20%, transparent),' +
        ' 0 0 24px -4px color-mix(in srgb, #22C55E 32%, transparent),' +
        ' 0 8px 32px -12px color-mix(in srgb, #22C55E 28%, transparent)',
      cardGlowSubtle:
        '0 0 0 1px color-mix(in srgb, #22C55E 10%, transparent),' +
        ' 0 0 14px -6px color-mix(in srgb, #22C55E 16%, transparent),' +
        ' 0 4px 12px -8px color-mix(in srgb, #22C55E 12%, transparent)',
      // Dark-mode widget chrome. Preserves the dark-glass recipe
      // that shipped before this contract — translucent near-black
      // bg + heavy blur + drop shadow with a faint accent ring at
      // 1px. The 6%-alpha green ring is intentionally subtle so it
      // reads as a halo, not a competing border.
      widgetBg: 'rgba(20, 20, 28, 0.7)',
      widgetBorder: 'rgba(255, 255, 255, 0.08)',
      widgetShadow:
        '0 12px 32px rgba(0, 0, 0, 0.35),' +
        ' 0 0 0 1px rgba(33, 193, 99, 0.06)',
      widgetBlur: 'blur(16px) saturate(160%)',
      // Dark-mode chrome tier — popovers, dropdowns, side panels.
      // Preserves the dense glass recipe that shipped pre-tokens
      // (rgba(20,20,28,0.92), blur(16px) saturate(160%), heavy drop
      // shadow) so dark-mode chrome visually matches the literals the
      // surfaces had before the swap.
      chromeBg: 'rgba(20, 20, 28, 0.92)',
      chromeBorder: 'rgba(255, 255, 255, 0.06)',
      chromeShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
      chromeBlur: 'blur(16px) saturate(160%)',
    },
    radii: RADII,
    fonts: FONTS,
    motion: MOTION,
  },
};
